/**
 * Tiny wrapper around Culqi's checkout SDK.
 *
 * Loads `https://checkout.culqi.com/js/v4` exactly once on demand, opens the
 * checkout modal with the right plan params, and resolves with the
 * card-token id (`tkn_*`) that the SPA must POST to `/api/billing/checkout`.
 *
 * We don't need React-Query here — this is a one-shot promise per click.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';

const SCRIPT_SRC = 'https://checkout.culqi.com/js/v4';
const SCRIPT_ID = 'culqi-checkout-script';

interface CulqiConfig {
  settings: {
    title: string;
    currency: string;
    amount: number;
    order?: string;
  };
  client?: { email?: string };
  options?: {
    lang?: 'auto' | 'es' | 'en';
    modal?: boolean;
    installments?: boolean;
    paymentMethods?: Record<string, boolean>;
  };
}

interface CulqiInstance {
  publicKey: string | null;
  settings: (s: CulqiConfig['settings']) => void;
  client?: (c: { email?: string }) => void;
  options: (o: CulqiConfig['options']) => void;
  open: () => void;
  close?: () => void;
  token?: { id: string; email?: string };
  order?: unknown;
  error?: { user_message?: string; merchant_message?: string };
}

declare global {
  interface Window {
    Culqi?: CulqiInstance;
    culqi?: () => void;
  }
}

function loadCulqiScript(): Promise<void> {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if (window.Culqi) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Culqi script failed to load')), {
        once: true,
      });
      return;
    }
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Culqi script failed to load'));
    document.head.appendChild(script);
  });
}

interface CheckoutArgs {
  amountCents: number;
  planName: string;
  email: string;
  currency?: string;
  orderRef?: string;
}

interface CheckoutResult {
  token: string;
  email?: string;
}

export function useCulqi() {
  const [ready, setReady] = useState(false);
  const [publicKey, setPublicKey] = useState<string | null>(null);
  const pendingResolve = useRef<((r: CheckoutResult) => void) | null>(null);
  const pendingReject = useRef<((e: Error) => void) | null>(null);

  // Boot once: fetch the public key and load the SDK.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get<{ public_key: string }>('/api/billing/config');
        if (!alive) return;
        setPublicKey(data.public_key);
        await loadCulqiScript();
        if (!alive) return;
        if (window.Culqi) {
          window.Culqi.publicKey = data.public_key;
        }
        // Culqi v4 invokes `window.culqi()` (lowercase) when the modal closes
        // with either a token, an order, or an error attached on `window.Culqi`.
        window.culqi = function culqiCallback() {
          const c = window.Culqi;
          if (!c) return;
          if (c.token?.id) {
            pendingResolve.current?.({ token: c.token.id, email: c.token.email });
          } else if (c.error) {
            const msg =
              c.error.user_message || c.error.merchant_message || 'Pago cancelado o rechazado.';
            pendingReject.current?.(new Error(msg));
          } else {
            // Modal closed without finishing — treat as user cancel.
            pendingReject.current?.(new Error('Pago cancelado.'));
          }
          pendingResolve.current = null;
          pendingReject.current = null;
        };
        setReady(true);
      } catch (err) {
        if (alive) {
          // Surface a "ready=false" state; the caller can hide the button or
          // show a fallback message.
          // eslint-disable-next-line no-console
          console.warn('Culqi init failed:', err);
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const checkout = useCallback(
    (args: CheckoutArgs): Promise<CheckoutResult> => {
      return new Promise((resolve, reject) => {
        if (!ready || !window.Culqi || !publicKey) {
          reject(new Error('Culqi todavía no está listo. Reintenta en un segundo.'));
          return;
        }
        // Stash callbacks so the global `window.culqi` handler can complete
        // them when the modal closes.
        pendingResolve.current = resolve;
        pendingReject.current = reject;

        // Safety net: si Culqi v4 NO dispara `window.culqi()` al cerrar el
        // modal (bug observado en algunos browsers cuando el usuario cierra
        // con la X o el backdrop), después de 10 min damos por cancelado.
        // Esto evita el "Procesando…" colgado para siempre.
        const timeoutId = window.setTimeout(() => {
          if (pendingReject.current) {
            pendingReject.current(new Error('Tiempo de espera agotado — el pago se canceló.'));
            pendingResolve.current = null;
            pendingReject.current = null;
          }
        }, 10 * 60_000);

        // Envolvemos resolve/reject para limpiar el timeout en cualquier salida.
        const cleanResolve = (r: CheckoutResult) => {
          window.clearTimeout(timeoutId);
          resolve(r);
        };
        const cleanReject = (e: Error) => {
          window.clearTimeout(timeoutId);
          reject(e);
        };
        pendingResolve.current = cleanResolve;
        pendingReject.current = cleanReject;

        window.Culqi.settings({
          title: 'AlphaConcreto',
          currency: args.currency ?? 'PEN',
          amount: args.amountCents,
          order: args.orderRef,
        });
        window.Culqi.client?.({ email: args.email });
        window.Culqi.options({
          lang: 'es',
          modal: true,
          installments: false,
          paymentMethods: { tarjeta: true, yape: true },
        });
        window.Culqi.open();
      });
    },
    [ready, publicKey],
  );

  /** Permite a un caller (ej. botón "Cancelar" en /carrito) abortar un
   *  checkout pendiente sin esperar al timeout. */
  const cancelCheckout = useCallback(() => {
    if (pendingReject.current) {
      pendingReject.current(new Error('Pago cancelado por el usuario.'));
      pendingResolve.current = null;
      pendingReject.current = null;
    }
    // Intenta cerrar el modal de Culqi si está abierto
    try {
      window.Culqi?.close?.();
    } catch {
      /* noop */
    }
  }, []);

  return { ready, publicKey, checkout, cancelCheckout };
}
